import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';

import Text from '../ui/Text';

import {
  PitchPrice,
  Price,
  PriceInformation,
  StayComponentContainer,
  StayInformation,
} from './AvailabilityPitches.style';

import {
  ToggleBodyWrapper,
  ToggleContentContainer,
  ToggleContent,
  ToggleDescription,
  ToggleTitle,
  NotAvailableText,
  LowAvailability,
  LowAvailabilityIcon,
  PitchError,
  BookedMessage,
  BookedIcon,
} from './Availability.style';

import { dictionaryItem } from '../../hocs/withDictionary';

import lightningBoltIcon from '../../static/images/icons/LightningBolt.svg';
import calendarTickIcon from '../../static/images/icons/CalendarTick.svg';
import PitchPricing from './PitchPricing';
import { AvailabilityContext } from './AvailabilitySearchQuery';

function PitchBody({
  description,
  disabled,
  name,
  minPricePerNight,
  partyString,
  numberOfNights,
  hasLowAvailability,
  hasDaysOnRequest,
  isSiteFullDuringStay,
  isPitchFullDuringStay,
  isBooked,
  totalPrice,
  id,
}) {
  const { availabilitySearch, availabilityTotal } = useContext(AvailabilityContext);
  const isLoading = availabilityTotal?.loading || availabilitySearch?.loading;

  const hasPrice =
    minPricePerNight
    && !isSiteFullDuringStay
    && !isPitchFullDuringStay;

  const partySegments = partyString.split(',');

  return (
    <ToggleBodyWrapper>
      <ToggleContentContainer>
        <ToggleContent disabled={disabled}>
          <ToggleTitle disabled={disabled}>{name}</ToggleTitle>
          <ToggleDescription>
            {description || 'No description provided.'}
          </ToggleDescription>
        </ToggleContent>
        <PitchPrice hasPrice={hasPrice}>
          <Price>
            {hasPrice &&
              <PriceInformation>
                {isLoading ? (
                  <span>Loading...</span>
                ) : (
                  <PitchPricing
                    numberOfNights={numberOfNights}
                    totalPrice={totalPrice}
                    minPricePerNight={minPricePerNight}
                    pitchId={id}
                  />
                )}
                <StayInformation>
                  {partySegments.map((partySegment, index) => <StayComponentContainer key={`PitchBody-StayComponentContainer-${partySegment}`}>{partySegment}{index !== (partySegments?.length - 1) ? ',' : ''}</StayComponentContainer>)}
                  {numberOfNights ? (
                    <StayComponentContainer> | {numberOfNights} nights</StayComponentContainer>
                  ) : (
                    <></>
                  )}
                </StayInformation>
              </PriceInformation>
            }
            {!isLoading && !hasPrice && (
              <NotAvailableText
                dictionary={dictionaryItem(
                  'AvailabilityPitches',
                  'NotAvailable',
                )}
              />
            )}
          </Price>
        </PitchPrice>
      </ToggleContentContainer>
      {hasPrice && hasLowAvailability && (
        <LowAvailability>
          <LowAvailabilityIcon icon={lightningBoltIcon} />
          <Text
            dictionary={dictionaryItem('AvailabilityPitches', 'SellingFast')}
          />
        </LowAvailability>
      )}
      {isBooked && (
        <BookedMessage>
          <BookedIcon icon={calendarTickIcon} />
          <Text>Your booked pitch</Text>
        </BookedMessage>
      )}
      {!isLoading && hasDaysOnRequest && (
        <PitchError
          dictionary={dictionaryItem('AvailabilityPitches', 'ContactUs')}
        />
      )}
      {!isLoading && isSiteFullDuringStay && !hasDaysOnRequest && (
        <PitchError
          dictionary={dictionaryItem('AvailabilityPitches', 'SiteFull')}
        />
      )}
      {!isLoading && !isSiteFullDuringStay && !hasDaysOnRequest && isPitchFullDuringStay && (
        <PitchError
          dictionary={dictionaryItem('AvailabilityPitches', 'PitchFull')}
        />
      )}
    </ToggleBodyWrapper>
  );
}

PitchBody.propTypes = {
  disabled: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  minPricePerNight: PropTypes.number,
  partyString: PropTypes.string,
  numberOfNights: PropTypes.number,
  hasLowAvailability: PropTypes.bool,
  hasDaysOnRequest: PropTypes.bool,
  isSiteFullDuringStay: PropTypes.bool,
  isPitchFullDuringStay: PropTypes.bool,
  isBooked: PropTypes.bool,
  totalPrice: PropTypes.number,
  id: PropTypes.string.isRequired,
};

PitchBody.defaultProps = {
  minPricePerNight: null,
  partyString: '',
  numberOfNights: null,
  hasLowAvailability: false,
  hasDaysOnRequest: false,
  isSiteFullDuringStay: false,
  isPitchFullDuringStay: false,
  isBooked: false,
  totalPrice: 0,
};

export default memo(PitchBody);
